/**
 * StepTemplate — choose a CV layout from 4 templates.
 *
 * activeTemplate is the version id (e.g. "classique"), but LayoutTemplate is the
 * visual layout. We set the layout on the current version via setActiveTemplate.
 * In this wizard context, we treat the layout as the template id for simplicity —
 * the user's first version defaults to a single version whose id matches the layout.
 */

import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LayoutTemplate } from '../../../types';

interface StepTemplateProps {
  activeLayout: LayoutTemplate;
  onLayoutChange: (layout: LayoutTemplate) => void;
}

const TEMPLATES: Array<{ id: LayoutTemplate; name: string; description: string; color: string }> = [
  {
    id: 'classique',
    name: 'Classique',
    description: 'Une colonne, sobre et professionnel. Idéal pour tous secteurs.',
    color: 'border-blue-500/40 bg-blue-500/5',
  },
  {
    id: 'moderne',
    name: 'Moderne',
    description: 'Deux colonnes avec barre latérale colorée. Design contemporain.',
    color: 'border-purple-500/40 bg-purple-500/5',
  },
  {
    id: 'ats',
    name: 'ATS',
    description: 'Optimisé pour les logiciels de tri automatique. Maximum lisibilité.',
    color: 'border-emerald-500/40 bg-emerald-500/5',
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Header sombre, mise en page executive. Pour les postes seniors.',
    color: 'border-orange-500/40 bg-orange-500/5',
  },
];

export function StepTemplate({ activeLayout, onLayoutChange }: StepTemplateProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Choisis ton modèle</h1>
        <p className="text-sm text-white/60">
          Tu pourras changer de modèle à tout moment depuis l'éditeur.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onLayoutChange(tpl.id)}
            className={cn(
              'relative flex flex-col items-start gap-3 p-5 rounded-2xl border text-left transition-all',
              activeLayout === tpl.id
                ? tpl.color + ' ring-2 ring-white/20'
                : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20',
            )}
          >
            {activeLayout === tpl.id && (
              <CheckCircle2 className="absolute top-3 right-3 size-4 text-white/60" />
            )}

            {/* Mini CV preview placeholder */}
            <div className="w-full h-20 rounded-lg bg-white/[0.04] border border-white/10 overflow-hidden flex items-center justify-center">
              <TemplateMiniPreview id={tpl.id} />
            </div>

            <div>
              <div className="text-sm font-bold text-white">{tpl.name}</div>
              <div className="text-xs text-white/50 mt-0.5">{tpl.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TemplateMiniPreview({ id }: { id: LayoutTemplate }) {
  // Simple schematic previews using CSS shapes
  if (id === 'classique') {
    return (
      <div className="w-full h-full p-2 space-y-1">
        <div className="h-2 w-16 bg-white/20 rounded" />
        <div className="h-1 w-10 bg-white/10 rounded" />
        <div className="mt-1 space-y-0.5">
          <div className="h-0.5 w-full bg-white/10 rounded" />
          <div className="h-0.5 w-5/6 bg-white/10 rounded" />
          <div className="h-0.5 w-4/6 bg-white/10 rounded" />
        </div>
      </div>
    );
  }
  if (id === 'moderne') {
    return (
      <div className="w-full h-full flex">
        <div className="w-1/3 h-full bg-purple-500/20 p-1 space-y-1">
          <div className="h-2 w-full bg-purple-400/30 rounded" />
          <div className="h-0.5 w-4/5 bg-purple-400/20 rounded" />
        </div>
        <div className="flex-1 p-2 space-y-1">
          <div className="h-0.5 w-full bg-white/10 rounded" />
          <div className="h-0.5 w-5/6 bg-white/10 rounded" />
          <div className="h-0.5 w-4/6 bg-white/10 rounded" />
        </div>
      </div>
    );
  }
  if (id === 'ats') {
    return (
      <div className="w-full h-full p-2 space-y-0.5">
        <div className="h-1.5 w-14 bg-white/20 rounded" />
        <div className="h-px w-full bg-white/20 mt-1" />
        <div className="h-0.5 w-full bg-white/10 rounded" />
        <div className="h-0.5 w-full bg-white/10 rounded" />
        <div className="h-0.5 w-4/5 bg-white/10 rounded" />
      </div>
    );
  }
  // pro
  return (
    <div className="w-full h-full">
      <div className="h-1/3 bg-white/[0.08] border-b border-white/10 p-1 flex items-end">
        <div className="h-1.5 w-12 bg-white/30 rounded" />
      </div>
      <div className="p-2 space-y-0.5">
        <div className="h-0.5 w-full bg-white/10 rounded" />
        <div className="h-0.5 w-5/6 bg-white/10 rounded" />
      </div>
    </div>
  );
}
